/**
 * The plan as a file the dietitian can keep — what the send dialog's Download
 * hands over.
 *
 * The document itself is the backend's: `GET /api/v1/meal-plans/{id}/pdf/`
 * renders it, headed with the dietitian's own brand, and this module is only
 * what happens to the bytes once they arrive. That is deliberate — what the
 * client is emailed and what the dietitian downloads are then the same
 * document, rather than two renderings that drift apart.
 *
 * It used to write the plan out as plain text here, for want of a generator.
 * The endpoint is the generator.
 */

/**
 * What naming the file actually needs of a plan, which is less than a whole
 * one: the send dialog is opened over a list row as well as over the plan in
 * the builder, and a row carries no `code`. See `SendablePlan`.
 */
export interface NameablePlan {
  id: string;
  /** The plan's own number, `NMP004`, where it is in hand. */
  code?: string;
  clientName: string;
}

/**
 * `NMP004-subham-patil.pdf` — the plan's own number, so two never collide.
 *
 * The `public_id` stands in where the number is not in hand, which is every
 * plan sent from a list row: it is the other thing no two plans share, so the
 * guarantee the name is here for holds either way.
 */
export const planFileName = (plan: NameablePlan): string => {
  const client = plan.clientName
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
  return `${plan.code || plan.id}${client ? `-${client}` : ""}.pdf`;
};

/**
 * Hands the file to the browser. The object URL is released straight after the
 * click — the download has already been handed off by then, and holding it
 * would keep the whole document in memory for the life of the tab.
 */
export const savePlanPdf = (plan: NameablePlan, pdf: Blob): void => {
  const url = URL.createObjectURL(pdf);

  const link = document.createElement("a");
  link.href = url;
  link.download = planFileName(plan);
  document.body.appendChild(link);
  link.click();
  link.remove();

  URL.revokeObjectURL(url);
};
